import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { setContractorDuplicatesScanning } from "slices/contractor/reducer";
import { toast } from "react-toastify";
import TableContainer from "Components/Common/TableContainer";
import {
  getContractorDuplicates,
  deleteContractor,
  scanContractorDuplicates,
  getContractorDuplicateScanStatus,
} from "common/services/contractors.service";
import { Flagged, SEMGMention } from "pages/Contractors/ContractorsCol";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import BulkActionBar from "Components/Common/BulkActionBar";
import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableFilters, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import * as moment from "moment";
import DeleteModal from "Components/Common/DeleteModal";
import { normalize } from "common/lib/normalize";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import { isAllowed } from "common/lib/permissionUtils";
import { useProfile } from "Components/Hooks/UserHooks";
import ResolveContractorDuplicate from "./ResolveContractorDuplicate";
import ViewContractorDuplicate from "./ViewContractorDuplicate";
import BulkResolveContractorDuplicates from "./BulkResolveContractorDuplicates";
import BulkMergeContractorDuplicates from "./BulkMergeContractorDuplicates";
import {
  FilterSearchBox,
} from "Components/Common/TableFilters";

interface FilterValue {
  resolved: string;
}

const AllContractors = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [deleteModal, setDeleteModal] = useState(false);

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));
  const defaultFilterValue: FilterValue = {
    resolved: "All",
  };
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions: any = [];

      if (filterValue?.resolved === "Resolved") {
        andConditions.push({
          resolved: true,
        });
      } else if (filterValue?.resolved === "Unresolved") {
        andConditions.push({
          $or: [
            {
              resolved: false,
            },
            {
              resolved: null,
            },
          ],
        });
      }

      andConditions.push({
        $or: [
          {
            contractorA: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
          {
            contractorB: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: {
            contractorA: {
              populate: {
                organizationType: "name",
                contracts: {
                  count: true,
                },
                semgMentions: {
                  count: true,
                },
                capacityAssessments: {
                  count: true,
                },
                riskAssessments: {
                  count: true,
                },
                pvrs: {
                  count: true,
                },
                performanceRatings: {
                  populate: {
                    performanceRatingType: true,
                  },
                },
              },
            },
            contractorB: {
              populate: {
                organizationType: "name",
                contracts: {
                  count: true,
                },
                semgMentions: {
                  count: true,
                },
                capacityAssessments: {
                  count: true,
                },
                riskAssessments: {
                  count: true,
                },
                pvrs: {
                  count: true,
                },
                performanceRatings: {
                  populate: {
                    performanceRatingType: true,
                  },
                },
              },
            },
          },
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getContractorDuplicates(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {
          autoClose: 500,
        });
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams, unCheckAll]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, searchValue, sorted, filterValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteContractor(selectedRecord);
      } catch (e: any) {
        toast.error("Error while deleting data", {});
        toast.success("Contractor deleted successfully", {});
      } finally {
        setDeleting(false);
        fetchUpdatedData();
        setDeleteModal(false);
      }
    }
  };

  const dispatch = useDispatch();
  const scanning = useSelector(
    (state: any) => state?.Contractors?.contractorDuplicates?.scanning
  );

  const scanPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [scanPercent, setScanPercent] = useState<number | null>(null);

  const stopScanPolling = useCallback(() => {
    if (scanPollRef.current) {
      clearInterval(scanPollRef.current);
      scanPollRef.current = null;
    }
  }, []);

  const pollScanStatus = useCallback(async () => {
    try {
      const response: any = await getContractorDuplicateScanStatus();
      const scanStatus = normalize(response);
      const status = scanStatus?.status;
      if (scanStatus?.total > 0) {
        setScanPercent(
          Math.min(
            100,
            Math.round(((scanStatus?.scanned || 0) / scanStatus.total) * 100)
          )
        );
      }
      if (status === "completed") {
        stopScanPolling();
        setScanPercent(null);
        dispatch(setContractorDuplicatesScanning(false));
        toast.success("Contractor duplicates scanned successfully", {});
        fetchUpdatedData();
      } else if (status === "failed") {
        stopScanPolling();
        setScanPercent(null);
        dispatch(setContractorDuplicatesScanning(false));
        toast.error("Error while scanning data", {});
      }
      // "running" (or no record yet) -> keep polling
    } catch (e: any) {
      // transient error; keep polling
    }
  }, [dispatch, fetchUpdatedData, stopScanPolling]);

  useEffect(() => {
    return () => stopScanPolling();
  }, [stopScanPolling]);

  // Resume the loading UI if a scan was already in progress (e.g. started
  // from another tab, or the page was reloaded mid-scan).
  useEffect(() => {
    (async () => {
      try {
        const response: any = await getContractorDuplicateScanStatus();
        if (normalize(response)?.status === "running") {
          dispatch(setContractorDuplicatesScanning(true));
          stopScanPolling();
          scanPollRef.current = setInterval(pollScanStatus, 3000);
        }
      } catch (e: any) {
        // ignore; button remains available to start a fresh scan
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScanContractorDuplicates = async () => {
    if (scanning) return;
    dispatch(setContractorDuplicatesScanning(true));
    try {
      await scanContractorDuplicates();
      stopScanPolling();
      scanPollRef.current = setInterval(pollScanStatus, 3000);
      pollScanStatus();
    } catch (e: any) {
      dispatch(setContractorDuplicatesScanning(false));
      toast.error("Error while scanning data", {});
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });

    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deleteContractor({ id });
          })
        );
        setDeleting(false);
        toast.success("Contractors deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const [viewContractorDuplicateModal, setViewContractorDuplicateModal] =
    useState<boolean>(false);
  const [resolveContractorDuplicateModal, setResolveContractorDuplicateModal] =
    useState<boolean>(false);
  const [
    selectedContractorDuplicateRecord,
    setSelectedContractorDuplicateRecord,
  ] = useState<any>({});
  const handleResolveContractorDuplicate = (data: any) => {
    setSelectedContractorDuplicateRecord(data);
    setResolveContractorDuplicateModal(true);
  };
  const handleViewContractorDuplicate = (data: any) => {
    setSelectedContractorDuplicateRecord(data);
    setViewContractorDuplicateModal(true);
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      { resource: "contractor-duplicate", action: "bulkResolve" },
      { resource: "contractor", action: "mergeContractors" },
    ]),
  });

  const [bulkResolveModal, setBulkResolveModal] = useState(false);
  const [bulkMergeModal, setBulkMergeModal] = useState(false);
  const selectedIds = useMemo(
    () => Array.from(selectedCheckBox).map((ele: any) => ele.value),
    [selectedCheckBox]
  );
  const selectedRecords = useMemo(
    () =>
      data.filter((record: any) =>
        selectedIds.includes(String(record.id))
      ),
    [data, selectedIds]
  );
  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Contractor Name",
        accessorKey: "contractorName",
        id: "contractorName",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;

          return (
            <>
              <div
                className="d-flex align-items-center mb-2"
                style={{ whiteSpace: "pre-wrap", width: "300px" }}
              >
                <Link
                  to={`/contractors/${rowData?.contractorA?.id}`}
                  className="fw-medium link-primary"
                >
                  {rowData?.contractorA?.contractorName || "-"}
                </Link>
              </div>
              <div
                className="d-flex align-items-center"
                style={{ whiteSpace: "pre-wrap", width: "300px" }}
              >
                <Link
                  to={`/contractors/${rowData?.contractorB?.id}`}
                  className="fw-medium link-primary"
                >
                  {rowData?.contractorB?.contractorName || "-"}
                </Link>
              </div>
            </>
          );
        },
      },
      {
        header: "Organization Type",
        accessorKey: "contractorA.organizationType.name",
        enableColumnFilter: false,
        enableSorting: false,
        id: "contractorA.organizationType.name",
        cell: (cell: any) => {
          const rowData = cell?.row?.original;
          return (
            <>
              <div className="mb-2">
                {rowData?.contractorA?.organizationType?.name || "-"}
              </div>
              <div className="">
                {rowData?.contractorB?.organizationType?.name || "-"}
              </div>
            </>
          );
        },
      },
      {
        header: "Flag Status",
        accessorKey: "flaggedStatus",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const contractorAStatuses = [];
          const contractorBStatuses = [];

          const rowData = cell?.row?.original;

          // Contractor A
          if (
            rowData?.contractorA?.semgMentions &&
            rowData?.contractorA?.semgMentions?.count > 0
          ) {
            contractorAStatuses.push(<SEMGMention />);
          }
          if (
            rowData?.contractorA?.performanceRatings?.some(
              (rating: any) =>
                rating?.isActive &&
                (rating?.performanceRatingType?.isFlag ||
                  rating?.performanceRatingType?.isDebarred)
            )
          ) {
            contractorAStatuses.push(<Flagged />);
          }
          if (contractorAStatuses.length === 0) {
            contractorAStatuses.push(
              <span className="badge bg-primary-subtle text-primary fs-11 me-2">
                N/A
              </span>
            );
          }

          // Contractor B
          if (
            rowData?.contractorB?.semgMentions &&
            rowData?.contractorB?.semgMentions?.count > 0
          ) {
            contractorBStatuses.push(<SEMGMention />);
          }

          if (
            rowData?.contractorB?.performanceRatings?.some(
              (rating: any) =>
                rating?.isActive &&
                (rating?.performanceRatingType?.isFlag ||
                  rating?.performanceRatingType?.isDebarred)
            )
          ) {
            contractorBStatuses.push(<Flagged />);
          }
          if (contractorBStatuses.length === 0) {
            contractorBStatuses.push(
              <span className="badge bg-primary-subtle text-primary fs-11 me-2">
                N/A
              </span>
            );
          }

          return (
            <>
              <div className="d-flex align-items-center mb-3">
                {contractorAStatuses}
              </div>
              <div className="d-flex align-items-center">
                {contractorBStatuses}
              </div>
            </>
          );
        },
      },
      {
        header: "Contracts",
        accessorKey: "contracts.count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;
          return (
            <>
              <div className="mb-2">
                {rowData?.contractorA?.contracts?.count ?? 0}
              </div>
              <div className="">
                {rowData?.contractorB?.contracts?.count ?? 0}
              </div>
            </>
          );
        },
      },

      {
        header: "Assessments",
        accessorKey: "assessments",
        enableColumnFilter: false,
        id: "capacityAssessments",
        enableSorting: false,
        sortingFn: () => null,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;

          const contractorAAssessments =
            (rowData?.capacityAssessments?.count ?? 0) +
            (rowData?.riskAssessments?.count ?? 0) +
            (rowData?.pvrs?.count ?? 0);

          const contractorBAssessments =
            (rowData?.capacityAssessments?.count ?? 0) +
            (rowData?.riskAssessments?.count ?? 0) +
            (rowData?.pvrs?.count ?? 0);
          return (
            <>
              <div className="mb-2">{contractorAAssessments}</div>
              <div className="">{contractorBAssessments}</div>
            </>
          );
        },
      },
      {
        header: "Resolved",
        accessorKey: "resolved",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;
          if (rowData?.resolved) {
            return (
              <>
                <span className="badge bg-success-subtle text-success fs-12 me-2">
                  Yes
                </span>
              </>
            );
          }
          return (
            <>
              <span className="badge bg-danger-subtle text-danger fs-12 me-2">
                No
              </span>
            </>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;
          if (rowData?.status === "duplicate") {
            return (
              <>
                <span className="badge border border-warning text-warning fs-12 me-2">
                  Duplicate
                </span>
              </>
            );
          }
          if (rowData?.status === "not_duplicate") {
            return (
              <>
                <span className="badge border border-success text-success fs-12 me-2">
                  Not Duplicate
                </span>
              </>
            );
          }
          return <div className=" ">Not Provided</div>;
        },
      },
      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;
          const contractorADate = moment(
            new Date(rowData?.contractorA?.updatedAt)
          ).format("DD MMM Y");
          const contractorBDate = moment(
            new Date(rowData?.contractorB?.updatedAt)
          ).format("DD MMM Y");
          return (
            <>
              <div className="mb-2">{contractorADate}</div>
              <div className="">{contractorBDate}</div>
            </>
          );
        },
      },
      {
        header: "Last Scanned",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;
          return (
            <div className="">
              {rowData?.createdAt
                ? moment(new Date(rowData.createdAt)).format("DD MMM Y")
                : "-"}
            </div>
          );
        },
      },
      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps?.row?.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="contractor-duplicate" action="resolve">
                  <DropdownItem
                    href={`#`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleResolveContractorDuplicate(rowData);
                    }}
                  >
                    <i className="ri-checkbox-circle-fill align-bottom me-2 text-muted"></i>{" "}
                    Resolve Duplicate
                  </DropdownItem>
                </Permission>

                <Permission resource="contractor-duplicate" action="findOne">
                  <DropdownItem
                    href={`#`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleViewContractorDuplicate(rowData);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View Duplicate
                  </DropdownItem>
                </Permission>

                <Permission resource="contractor" action="mergeContractors">
                  <DropdownItem
                    href={`#`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        `/contractors/merge/${rowData?.contractorA?.id}?target=${rowData?.contractorB?.id}`
                      );
                    }}
                  >
                    <i className="ri-arrow-left-right-line align-bottom me-2 text-muted"></i>{" "}
                    Merge
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate]
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => handleDelete()}
        onCloseClick={() => setDeleteModal(false)}
        loading={deleting}
      />
      <DeleteModal
        show={deleteMultipleModal}
        onDeleteClick={() => handleDeleteMultiple()}
        onCloseClick={() => setDeleteMultipleModal(false)}
        loading={deleting}
      />
      <ResolveContractorDuplicate
        isModalOpen={resolveContractorDuplicateModal}
        toggle={() => setResolveContractorDuplicateModal(false)}
        selectedRecord={selectedContractorDuplicateRecord}
        fetchData={fetchUpdatedData}
      />
      <ViewContractorDuplicate
        isModalOpen={viewContractorDuplicateModal}
        toggle={() => setViewContractorDuplicateModal(false)}
        selectedRecord={selectedContractorDuplicateRecord}
      />
      <BulkResolveContractorDuplicates
        isModalOpen={bulkResolveModal}
        toggle={() => setBulkResolveModal(false)}
        selectedIds={selectedIds}
        fetchData={fetchUpdatedData}
      />
      <BulkMergeContractorDuplicates
        isModalOpen={bulkMergeModal}
        toggle={() => setBulkMergeModal(false)}
        selectedRecords={selectedRecords}
        fetchData={fetchUpdatedData}
      />
      <Permissions
        checks={[
          { resource: "contractor-duplicate", action: "bulkResolve" },
          { resource: "contractor", action: "mergeContractors" },
        ]}
      >
        <BulkActionBar
          count={selectedIds.length}
          onClear={unCheckAll}
          itemLabel="duplicate"
        >
          <Permission resource="contractor-duplicate" action="bulkResolve">
            <Button
              color="primary"
              size="sm"
              className="material-shadow-none"
              onClick={() => setBulkResolveModal(true)}
            >
              <i className="ri-checkbox-circle-line align-bottom me-1"></i>{" "}
              Resolve
            </Button>
          </Permission>
          <Permission resource="contractor" action="mergeContractors">
            <Button
              color="secondary"
              size="sm"
              className="material-shadow-none btn-soft-secondary"
              onClick={() => setBulkMergeModal(true)}
            >
              <i className="ri-arrow-left-right-line align-bottom me-1"></i>{" "}
              Merge
            </Button>
          </Permission>
        </BulkActionBar>
      </Permissions>
      <Card>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col lg={12}>
              <FilterSearchBox
                id="contractor-duplicate-search"
                placeholder="Search contractors..."
                value={searchValue}
                onChange={(value: string) => {
                  setSearchValue(value);
                  setPage(1);
                }}
              />
            </Col>

          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={8}>
              <h5 className="card-title mb-1">Contractor Duplicates</h5>
              <p className="mb-0 text-muted">
                This is a comprehensive list of contractor duplicates that have
                been automatically identified by the duplicate finder algorithm.
              </p>
            </Col>
            <div className="col-md-auto ms-auto">
              <div className="d-flex gap-2 align-items-center">
                <Permission resource="contractor-duplicate" action="scan">
                  <UncontrolledDropdown className="card-header-dropdown me-2">
                    <DropdownToggle
                      tag="a"
                      className="text-reset"
                      role="button"
                    >
                      <span className="fw-semibold text-uppercase fs-12 me-1">
                        Displaying :{" "}
                      </span>
                      <span className="text-muted">
                        {filterValue?.resolved === "All"
                          ? "All"
                          : filterValue?.resolved === "Resolved"
                          ? "Resolved"
                          : "Unresolved"}
                        <i className="mdi mdi-chevron-down ms-1"></i>
                      </span>
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end">
                      <DropdownItem
                        onClick={() => {
                          setFilterValue({
                            ...filterValue,
                            resolved: "All",
                          });
                        }}
                      >
                        All
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => {
                          setFilterValue({
                            ...filterValue,
                            resolved: "Resolved",
                          });
                        }}
                      >
                        Resolved
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => {
                          setFilterValue({
                            ...filterValue,
                            resolved: "Unresolved",
                          });
                        }}
                      >
                        Unresolved
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </Permission>
                <Permission resource="contractor-duplicate" action="scan">
                  <Button
                    color="primary"
                    disabled={scanning}
                    onClick={() => {
                      handleScanContractorDuplicates();
                    }}
                  >
                    {scanning ? (
                      <i className="ri-loader-2-line animate-spin align-bottom me-1"></i>
                    ) : (
                      <i className="ri-refresh-line align-bottom me-1"></i>
                    )}
                    {scanning
                      ? `Scanning... ${
                          scanPercent !== null ? `${scanPercent}%` : ""
                        }`
                      : "Scan Contractor Duplicates"}
                  </Button>
                </Permission>
              </div>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          <TableContainer
            loading={loading}
            columns={columns}
            data={data || []}
            pagination={pagination}
            setPagination={setPagination}
            sorted={sorted}
            setSorted={setSorted}
            onPageChange={onPageChange}
            FilterSection={() => {}}
            totalRows={meta?.pagination?.total || 0}
            totalPages={meta?.pagination?.pageCount || 0}
            searchValue={searchValue}
            filterValue={filterValue}
            columnVisibility={columnVisibility}
            divClass="table-responsive table-card mb-4"
            tableClass="align-middle table-nowrap mb-0 table-striped"
            theadClass="table-light table-nowrap"
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default AllContractors;
